import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  FileText, Download, Building2, MapPin, Receipt, 
  User, Calendar, Bed, Edit2, Loader2, IndianRupee
} from 'lucide-react';

export default function InvoiceReviewDialog({ open, onOpenChange, booking, onInvoiceGenerated }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [guest, setGuest] = useState(null);
  const [room, setRoom] = useState(null);
  const [editingBillTo, setEditingBillTo] = useState(false);
  
  // Bill To details (editable)
  const [billTo, setBillTo] = useState({
    company_name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    gstin: '',
    contact_person: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    if (open && booking) {
      loadInvoiceData();
    }
  }, [open, booking]);

  const loadInvoiceData = async () => {
    setLoading(true);
    try {
      // Load guest details
      const guestRes = await api.get(`/guests/${booking.guest_id}`);
      setGuest(guestRes.data);
      
      // Set initial bill to from guest data
      setBillTo({
        company_name: guestRes.data.company_name || '',
        address: guestRes.data.address || '',
        city: guestRes.data.city || '',
        state: guestRes.data.state || '',
        pincode: guestRes.data.pincode || '',
        gstin: guestRes.data.gstin || '',
        contact_person: guestRes.data.name || '',
        email: guestRes.data.email || '',
        phone: guestRes.data.phone || ''
      });
      
      // Load room details if available
      if (booking.room_id) {
        try {
          const roomRes = await api.get(`/rooms/${booking.room_id}`);
          setRoom(roomRes.data);
        } catch (e) {
          console.log('Room not found');
        }
      }
      
      // Try to load existing invoice or checkout data
      try {
        const invoicesRes = await api.get(`/invoices?booking_id=${booking.id}`);
        if (invoicesRes.data && invoicesRes.data.length > 0) {
          setInvoiceData(invoicesRes.data[0]);
          // If invoice has bill_to data, use it
          if (invoicesRes.data[0].bill_to) {
            setBillTo(prev => ({
              ...prev,
              ...invoicesRes.data[0].bill_to
            }));
          }
        }
      } catch (e) {
        // No existing invoice, calculate from booking
        const checkoutRes = await api.get(`/checkout/${booking.id}`);
        setInvoiceData({
          room_charges: checkoutRes.data.room_charges || 0,
          restaurant_charges: 0,
          service_charges: 0,
          tax: checkoutRes.data.tax || 0,
          total: checkoutRes.data.total || 0,
          amount_paid: booking.advance_paid || 0,
          balance_due: (checkoutRes.data.total || 0) - (booking.advance_paid || 0)
        });
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      toast.error(getErrorMessage(error, 'Failed to load invoice data'));
    } finally {
      setLoading(false);
    }
  };

  const handleBillToChange = (field, value) => {
    setBillTo(prev => ({ ...prev, [field]: value }));
  };

  // Save bill to information only (no download)
  const handleSaveAndClose = async () => {
    setGenerating(true);
    try {
      let invoiceId = invoiceData?.id;
      
      // If no existing invoice, create one
      if (!invoiceId) {
        const createRes = await api.post(`/invoices/generate/${booking.id}`, {
          bill_to: billTo
        });
        invoiceId = createRes.data.invoice?.id || createRes.data.id;
      } else {
        // Update existing invoice with bill_to details
        await api.put(`/invoices/${invoiceId}`, {
          bill_to: billTo
        });
      }
      
      toast.success('Invoice information saved!');
      onOpenChange(false);
      if (onInvoiceGenerated) onInvoiceGenerated();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(getErrorMessage(error, 'Failed to save invoice information'));
    } finally {
      setGenerating(false);
    }
  };

  // Save bill to information and download PDF
  const handleSaveAndDownload = async () => {
    setGenerating(true);
    try {
      let invoiceId = invoiceData?.id;
      
      // If no existing invoice, create one
      if (!invoiceId) {
        const createRes = await api.post(`/invoices/generate/${booking.id}`, {
          bill_to: billTo
        });
        invoiceId = createRes.data.invoice?.id || createRes.data.id;
      } else {
        // Update existing invoice with bill_to details
        await api.put(`/invoices/${invoiceId}`, {
          bill_to: billTo
        });
      }
      
      if (!invoiceId) {
        throw new Error('Could not get invoice ID');
      }
      
      // Download PDF
      const pdfResponse = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${booking.id.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice saved and downloaded!');
      onOpenChange(false);
      if (onInvoiceGenerated) onInvoiceGenerated();
    } catch (error) {
      console.error('Save & download error:', error);
      toast.error(getErrorMessage(error, 'Failed to save and download invoice'));
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Invoice Review
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading invoice data...</span>
          </div>
        ) : (
          <ScrollArea className="max-h-[65vh] pr-4">
            <div className="space-y-4">
              {/* Booking Summary */}
              <Card className="border-gray-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    Booking Details
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Booking ID</p>
                      <p className="font-mono font-medium">{booking.id?.substring(0, 8)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Guest Name</p>
                      <p className="font-medium">{guest?.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Room</p>
                      <p className="font-medium">{room?.room_number || booking.room_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Stay Period</p>
                      <p className="font-medium text-xs">
                        {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bill To Section */}
              <Card className="border-blue-200 bg-blue-50/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-blue-600" />
                      Bill To Details
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingBillTo(!editingBillTo)}
                      className="h-7 text-xs"
                    >
                      <Edit2 className="h-3 w-3 mr-1" />
                      {editingBillTo ? 'Done' : 'Edit'}
                    </Button>
                  </div>

                  {editingBillTo ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="md:col-span-2">
                        <Label className="text-xs text-gray-600">Company / Organization Name</Label>
                        <Input
                          value={billTo.company_name}
                          onChange={(e) => handleBillToChange('company_name', e.target.value)}
                          placeholder="Enter company name (leave blank for individual)"
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Contact Person</Label>
                        <Input
                          value={billTo.contact_person}
                          onChange={(e) => handleBillToChange('contact_person', e.target.value)}
                          placeholder="Contact person name"
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">GSTIN</Label>
                        <Input
                          value={billTo.gstin}
                          onChange={(e) => handleBillToChange('gstin', e.target.value.toUpperCase())}
                          placeholder="e.g., 29ABCDE1234F1Z5"
                          className="mt-1 h-9 font-mono"
                          maxLength={15}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs text-gray-600">Address</Label>
                        <Textarea
                          value={billTo.address}
                          onChange={(e) => handleBillToChange('address', e.target.value)}
                          placeholder="Street address"
                          className="mt-1 min-h-[60px]"
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">City</Label>
                        <Input
                          value={billTo.city}
                          onChange={(e) => handleBillToChange('city', e.target.value)}
                          placeholder="City"
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">State</Label>
                        <Input
                          value={billTo.state}
                          onChange={(e) => handleBillToChange('state', e.target.value)}
                          placeholder="State"
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">PIN Code</Label>
                        <Input
                          value={billTo.pincode}
                          onChange={(e) => handleBillToChange('pincode', e.target.value)}
                          placeholder="PIN Code"
                          className="mt-1 h-9"
                          maxLength={6}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Email</Label>
                        <Input
                          value={billTo.email}
                          onChange={(e) => handleBillToChange('email', e.target.value)}
                          placeholder="Email address"
                          className="mt-1 h-9"
                          type="email"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Phone</Label>
                        <Input
                          value={billTo.phone}
                          onChange={(e) => handleBillToChange('phone', e.target.value)}
                          placeholder="Phone number"
                          className="mt-1 h-9"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-3 border border-blue-100">
                      {billTo.company_name ? (
                        <>
                          <p className="font-semibold text-gray-800">{billTo.company_name}</p>
                          {billTo.contact_person && (
                            <p className="text-sm text-gray-600">Attn: {billTo.contact_person}</p>
                          )}
                        </>
                      ) : (
                        <p className="font-semibold text-gray-800">{billTo.contact_person || guest?.name || '-'}</p>
                      )}
                      {billTo.address && <p className="text-sm text-gray-600 mt-1">{billTo.address}</p>}
                      {(billTo.city || billTo.state || billTo.pincode) && (
                        <p className="text-sm text-gray-600">
                          {[billTo.city, billTo.state, billTo.pincode].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {billTo.gstin && (
                        <p className="text-sm text-gray-700 mt-2 font-mono">
                          <span className="text-gray-500">GSTIN:</span> {billTo.gstin}
                        </p>
                      )}
                      {billTo.email && <p className="text-sm text-gray-600">{billTo.email}</p>}
                      {billTo.phone && <p className="text-sm text-gray-600">{billTo.phone}</p>}
                      
                      {!billTo.company_name && !billTo.address && !billTo.gstin && (
                        <p className="text-sm text-amber-600 italic">
                          Click "Edit" to add company/billing details for GST invoice
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Invoice Summary */}
              <Card className="border-gray-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <IndianRupee className="h-4 w-4 text-green-600" />
                    Invoice Summary
                  </h3>
                  
                  <div className="space-y-2">
                    {invoiceData?.room_charges > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Room Charges</span>
                        <span className="font-medium">{formatCurrency(invoiceData.room_charges)}</span>
                      </div>
                    )}
                    {invoiceData?.restaurant_charges > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Restaurant Charges</span>
                        <span className="font-medium">{formatCurrency(invoiceData.restaurant_charges)}</span>
                      </div>
                    )}
                    {invoiceData?.service_charges > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Service Charges</span>
                        <span className="font-medium">{formatCurrency(invoiceData.service_charges)}</span>
                      </div>
                    )}
                    
                    <Separator className="my-2" />
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">
                        {formatCurrency(
                          (invoiceData?.room_charges || 0) + 
                          (invoiceData?.restaurant_charges || 0) + 
                          (invoiceData?.service_charges || 0)
                        )}
                      </span>
                    </div>
                    
                    {invoiceData?.discount > 0 && (
                      <div className="flex justify-between text-sm text-red-600">
                        <span>Discount</span>
                        <span>- {formatCurrency(invoiceData.discount)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax (GST)</span>
                      <span className="font-medium">{formatCurrency(invoiceData?.tax)}</span>
                    </div>
                    
                    <Separator className="my-2" />
                    
                    <div className="flex justify-between text-base font-bold">
                      <span className="text-gray-800">Grand Total</span>
                      <span className="text-blue-600">{formatCurrency(invoiceData?.total)}</span>
                    </div>
                    
                    {invoiceData?.amount_paid > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Amount Paid</span>
                        <span>- {formatCurrency(invoiceData.amount_paid)}</span>
                      </div>
                    )}
                    
                    {invoiceData?.balance_due !== undefined && (
                      <div className="flex justify-between text-base font-bold">
                        <span className="text-gray-800">Balance Due</span>
                        <span className={invoiceData.balance_due > 0 ? 'text-red-600' : 'text-green-600'}>
                          {formatCurrency(invoiceData.balance_due)}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* GST Note */}
              {billTo.gstin && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <strong>GST Invoice:</strong> This invoice will include GSTIN {billTo.gstin} for input tax credit.
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
            data-testid="cancel-invoice-btn"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleSaveAndClose}
            disabled={loading || generating}
            className="w-full sm:w-auto border-blue-300 text-blue-600 hover:bg-blue-50"
            data-testid="save-close-invoice-btn"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Save & Close
              </>
            )}
          </Button>
          <Button
            onClick={handleSaveAndDownload}
            disabled={loading || generating}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
            data-testid="save-download-invoice-btn"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Save & Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
